// Inline pricing tag rendered on every public room card.
//
// Renders the brand-canonical 4-line price block via <RoomPriceBlock/>
// and reveals every board plan (per person and night) inside a hover /
// focus / tap popover. All copy is centralised in `pricingDefaults.js`.

import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFormatPrice, useCurrency } from "@/contexts/CurrencyContext";
import { getRoomPricing } from "@/lib/hotelPricing";
import { BOARD_BASIS_INCLUDED_LABELS, pickOccupancyBasisLabel, pickPerPersonPerNightLabel } from "@/lib/pricingDefaults";
import RoomPriceBlock from "@/components/pricing/RoomPriceBlock";
import ReserveThisHotelButton from "@/components/ReserveThisHotelButton";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Popover header — "All board options · <CUR> per person and night".
const PLANS_TITLE_COPY = {
  en: (cur) => `All board options · ${cur} per person and night`,
  fr: (cur) => `Toutes les options · ${cur} par personne et par nuit`,
  es: (cur) => `Todas las opciones · ${cur} por persona y noche`,
};

const PLAN_ORDER = ["room_only", "breakfast", "half_board", "full_board"];

const RoomPriceTag = ({ room, hotelSlug, testid = "room-price-tag" }) => {
  const { lang } = useLanguage();
  const { fromMAD } = useFormatPrice();
  const { currency } = useCurrency();
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const pricing = getRoomPricing(room, hotelSlug);
  if (!pricing || !pricing.perPerson || pricing.perPerson.fromMAD == null) return null;

  const { occupancy, plans: pppPlans, fromMAD: pppFromMAD, cheapestKey } = pricing.perPerson;
  // Determine which board basis the FROM price corresponds to.
  const fromBasisKey =
    cheapestKey === "breakfast"
      ? "bb"
      : cheapestKey === "half_board"
      ? "hb"
      : cheapestKey === "full_board"
      ? "fb"
      : "room_only";

  const handleEnter = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div
      ref={wrapperRef}
      className="relative inline-block"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className="text-left group/price"
        data-testid={`${testid}-trigger`}
      >
        <RoomPriceBlock
          occupancy={occupancy}
          amountMAD={pppFromMAD}
          boardBasis={fromBasisKey}
          variant="card"
          testid={`${testid}-block`}
        />
        <span className="inline-flex items-center gap-1 mt-2 text-[10px] tracking-[0.22em] uppercase text-[#C16542] group-hover/price:gap-1.5 transition-all">
          {pick({ en: "See all board options", fr: "Voir toutes les options", es: "Ver todas las opciones" }, lang)}
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
            strokeWidth={1.8}
          />
        </span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={PLANS_TITLE_COPY[lang]?.(currency) || PLANS_TITLE_COPY.en(currency)}
          className="absolute z-30 mt-3 left-0 w-80 bg-[#FDFBF7] border border-[#2C2621]/15 shadow-xl p-5"
          data-testid={`${testid}-popover`}
        >
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-2 pb-2 border-b border-[#2C2621]/10">
            {PLANS_TITLE_COPY[lang]?.(currency) || PLANS_TITLE_COPY.en(currency)}
          </p>
          <p className="text-[11px] italic text-[#5C5248] mb-3">
            {pickOccupancyBasisLabel(occupancy, lang)}
          </p>
          <ul className="space-y-2.5">
            {PLAN_ORDER.map((key) => {
              const value = pppPlans?.[key];
              const amount = value != null ? fromMAD(value) : null;
              const isLowest = value != null && value === pppFromMAD;
              const boardKey =
                key === "breakfast" ? "bb" : key === "half_board" ? "hb" : key === "full_board" ? "fb" : "room_only";
              return (
                <li
                  key={key}
                  className={`flex items-center justify-between gap-4 ${isLowest ? "font-medium" : ""}`}
                  data-testid={`${testid}-plan-${key}`}
                >
                  <span className="text-sm text-[#2C2621]">
                    {pick(BOARD_BASIS_INCLUDED_LABELS[boardKey], lang)}
                  </span>
                  {amount ? (
                    <span className="text-right whitespace-nowrap">
                      <span className={`text-sm tabular-nums ${isLowest ? "text-[#C16542]" : "text-[#2C2621]"}`}>
                        {amount}
                      </span>
                      <span className="ml-1 text-[10px] text-[#5C5248]">
                        / {pickPerPersonPerNightLabel(lang).split(" ")[0]}
                      </span>
                    </span>
                  ) : (
                    <span className="text-xs text-[#5C5248]/70 italic">—</span>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="mt-4 pt-4 border-t border-[#2C2621]/10">
            <ReserveThisHotelButton
              hotelSlug={hotelSlug}
              size="sm"
              testid={`${testid}-reserve`}
              className="w-full"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomPriceTag;
